/**
 * Carrier view of call-offs: the confirmed, dispatched and delivered loads of
 * every customer the viewer carries for, from all of that customer's
 * suppliers. Read-only and price-free.
 *
 * Rendered by the Logistics Call-Offs page (logistics companies) and inside
 * the Purchasing Call-Off tab for a supplier a customer marked as its
 * Logistics Provider (see CallOffTab).
 */
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { logisticsAPI, apiErrorMessage, type LogisticsCallOff } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { usePagination } from '../../../hooks/usePagination';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Pagination } from '../../ui/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { AlertCircle, Eye, Loader2, Package, Search } from 'lucide-react';
import CallOffDetailModal from '../management/CallOffDetailModal';
import LogisticsStatusBadge from './LogisticsStatusBadge';

const PAGE_SIZE = 25;

export default function LogisticsCallOffsView() {
  const { formatDate } = useFormatters();
  const pagination = usePagination({ pageSize: PAGE_SIZE, scrollToTop: false });
  const { currentPage, pageSize, setFromResponse, setCurrentPage, goToPage, setPageSize } = pagination;
  const [callOffs, setCallOffs] = useState<LogisticsCallOff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  // CR-7732: the haulier opens the same call-off detail the customer and the
  // supplier see, rather than a reduced copy of it. The modal fetches the
  // call-off itself, so only its id travels from this list.
  // CR-8613: a notice links here with ?calloff_id=, which opens that load.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCallOffId, setSelectedCallOffId] = useState<number | null>(() => {
    const linked = Number(searchParams.get('calloff_id'));
    return Number.isInteger(linked) && linked > 0 ? linked : null;
  });

  // A second notice clicked while the page is open changes only the address.
  useEffect(() => {
    const linked = Number(searchParams.get('calloff_id'));
    if (Number.isInteger(linked) && linked > 0) setSelectedCallOffId(linked);
  }, [searchParams]);

  const closeDetail = () => {
    setSelectedCallOffId(null);
    if (searchParams.has('calloff_id')) {
      const next = new URLSearchParams(searchParams);
      next.delete('calloff_id');
      setSearchParams(next, { replace: true });
    }
  };

  // Debounce typing; a new search always starts from page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(search);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, setCurrentPage]);

  const fetchCallOffs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { page: currentPage, page_size: pageSize };
      if (appliedSearch) params['search'] = appliedSearch;
      const res = await logisticsAPI.getCallOffs(params);
      const rows = res.data.results ?? [];
      setCallOffs(rows);
      setFromResponse(res.data.count ?? rows.length, rows.length);
    } catch (err: unknown) {
      console.error('Failed to load logistics call-offs:', err);
      setCallOffs([]);
      setError(apiErrorMessage(err, 'Could not load call-offs. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, appliedSearch, setFromResponse]);

  useEffect(() => {
    fetchCallOffs();
  }, [fetchCallOffs]);

  const viewDetail = (id: number) => setSelectedCallOffId(id);

  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by reference, supplier, or customer..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          aria-label="Search call-offs"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16" data-testid="logistics-loading">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-16" role="alert">
              <AlertCircle className="h-10 w-10 mx-auto mb-3 text-destructive" />
              <p className="text-sm text-destructive mb-4">{error}</p>
              <Button variant="outline" onClick={() => fetchCallOffs()}>Try again</Button>
            </div>
          ) : callOffs.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{appliedSearch ? 'No call-offs match your search' : 'No Supplier Confirmed, Dispatched or Delivered call-offs yet'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Pickup Date</TableHead>
                  <TableHead>Dispatched</TableHead>
                  <TableHead className="text-right">Items</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead><span className="sr-only">View</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {callOffs.map((co) => (
                  <TableRow key={co.id} className="cursor-pointer hover:bg-muted/50" onClick={() => viewDetail(co.id)}>
                    <TableCell className="font-medium">{co.reference}</TableCell>
                    <TableCell><LogisticsStatusBadge status={co.detail_status} logisticsConfirmedAt={co.logistics_confirmed_at} /></TableCell>
                    <TableCell>{co.supplier_name}</TableCell>
                    <TableCell>{co.customer_name}</TableCell>
                    <TableCell>{co.pickup_date ? formatDate(co.pickup_date) : '-'}</TableCell>
                    <TableCell>{co.dispatched_at ? formatDate(co.dispatched_at) : '-'}</TableCell>
                    <TableCell className="text-right">{co.total_items}</TableCell>
                    <TableCell className="text-right">{co.total_quantity}</TableCell>
                    <TableCell>{co.destination_name || '-'}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost" size="icon" aria-label={`View ${co.reference}`}
                        onClick={(e) => { e.stopPropagation(); viewDetail(co.id); }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {!error && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          itemsPerPage={pageSize}
          servedPerPage={pagination.servedPageSize}
          onPageChange={goToPage}
          onPageSizeChange={setPageSize}
          itemLabel="call-offs"
        />
      )}

      <CallOffDetailModal
        open={selectedCallOffId !== null}
        callOffId={selectedCallOffId}
        onClose={closeDetail}
        onUpdated={fetchCallOffs}
      />
    </div>
  );
}
